<template>
  <main class="chat-page" v-bind:class="{'chat-page--preview': isPreview()}">
    <transition name="fade">
      <ChapterView
        v-if="showChapterView"
        :htmlText="showChapterView"
      ></ChapterView>
    </transition>

    <Bar :chat="chat" />
    <transition name="slide-down">
      <Notification
        v-show="showNotification && nextChatId"
        :nextChatId="nextChatId"
        badge="new"
        :textHeader="notificationData.textHeader"
        :textDesc="notificationData.textDesc"
      ></Notification>
    </transition>

    <div
      class="chat-page__wrapper"
      ref="chatInner"
      v-chat-scroll="{always: true}"
    >
      <div
        class="message-group-wrapper"
        v-for="(message, index) in chatMessages"
        :key="index"
      >
        <MessageGroup
          :position="getMessagePosition(message[0].author)"
          :name="getMessageAuthorName(message[0].author)"
          :thumbnail="getMessageAuthorAvatar(message[0].author)"
        >
          <div class="message-wrapper" v-for="item in message" :key="item.id">
            <Message
              v-if="item.content_type == 'text'"
              v-html="item.value"
            ></Message>
            <Message
              v-else-if="item.content_type == 'image'"
              :image="item.value"
            ></Message>
            <Message
              v-else-if="item.content_type == 'video'"
              :video="item.value"
              :messages="message"
              :wasPlayedEarlier="item.wasPlayedEarlier"
            ></Message>
          </div>
        </MessageGroup>
      </div>
      <!-- / message-group-wrapper -->

      <TextLoader v-if="messageLoading" :position="loaderPosition" />
    </div>
    <!-- / chat-page__wrapper -->

    <transition name="slide-up">
      <AnswerBox
        v-if="answer"
        :values="answer.value"
        :parentId="answer.parentId"
        :childIds="answer.childIds"
        :saveAnswer="answer.saveAnswer"
        :additionalScript="getAdditionalScript(answer)"
        :type="answer.content_type"
        right
      ></AnswerBox>
    </transition>
  </main>
</template>

<script>
import Notification from "@/components/Notification.vue";
import Bar from "@/components/Bar.vue";
import MessageGroup from "@/components/MessageGroup.vue";
import Message from "@/components/Message.vue";
import AnswerBox from "@/components/AnswerBox.vue";
import TextLoader from "@/components/TextLoader.vue";
import ChapterView from "@/components/ChapterView.vue";

function startProcessing(
  script,
  lastMessageIndex,
  lastMessage,
  videoUrl,
  videoPoster,
  $this
) {
  if (script && !$this.isProcessing) {
    if (!videoUrl && !videoPoster) {
      $this.$processMessages(script, $this.chat, $this.$store);
      $this.$store.dispatch("toggleProcessing", {
        chat: $this.chat,
        value: true
      });
    }
  }
}

export default {
  name: "Chat",
  components: {
    Notification,
    Bar,
    MessageGroup,
    Message,
    AnswerBox,
    TextLoader,
    ChapterView
  },
  data() {
    return {
      processing: false
    };
  },
  methods: {
    getAdditionalScript(answer) {
      const additionalScript = answer.additionalScript
        ? answer.additionalScript
        : {};
      return additionalScript;
    },
    getMessagePosition(author) {
      return !author ? "right" : "left";
    },
    getMessageAuthorName(author) {
      return !author ? "Meg" : author;
    },
    getMessageAuthorAvatar(author) {
      const authorData = this.$store.getters.getChatAuthorByName(author);
      if (authorData) {
        return [authorData.image.path];
      }
    },
    initProcessing() {
      const script = this.chat && this.chat.script || [];
      if (script.length > 0) {
        this.processing = true;
        // get script data
        const lastMessageIndex = this.chat.messages.length - 1;
        const lastMessage = this.chat.messages[lastMessageIndex];
        const videoUrl = lastMessage.value.url ? lastMessage.value.url : false;
        const videoPoster = lastMessage.value.cover
          ? lastMessage.value.cover
          : false;

        startProcessing(
          script,
          lastMessageIndex,
          lastMessage,
          videoUrl,
          videoPoster,
          this
        );
      }
    },
    isPreview() {
      const mode = this.$store.getters.getMode;
      return mode && mode === 'preview';
    }
  },
  computed: {
    showChapterView() {
      return this.$store.getters.getChapterViewValue();
    },
    isProcessing() {
      return this.$store.getters.getProcessingValue(this.$route.params.id);
    },
    notificationData() {
      const chat = this.$store.getters.getChatById(this.$route.params.id);
      return chat && chat.notification || {};
    },
    showNotification() {
      const chat = this.$store.getters.getChatById(this.$route.params.id);
      return chat && chat.notificationShow;
    },
    answer() {
      return this.$store.getters.getAnswer(this.$route.params.id);
    },
    loaderPosition() {
      return this.$store.getters.getLoaderPosition();
    },
    chatId() {
      return this.$route.params.id;
    },
    chat() {
      const chatId = this.chatId;
      return this.$store.getters.getChatById(chatId);
    },
    nextChatId() {
      return this.chat && this.chat.notification.nextChatId
        ? this.chat.notification.nextChatId
        : "";
    },
    chatMessages() {
      let uniqueGroupMessages = [];
      let formattedMessages = [];
      let groupMessages = [];
      let lastEl = [];

      if (this.chat && this.chat.messages) {
        this.chat.messages.forEach(el => {
          if (lastEl.author == el.author) {
            // multiple message
            formattedMessages.pop(); // remove last array element

            // create a group of messages
            groupMessages.push(lastEl, el);
            uniqueGroupMessages = [...new Set(groupMessages)];
            formattedMessages.push(uniqueGroupMessages);
          } else {
            // single message
            groupMessages = [];
            uniqueGroupMessages = [];
            formattedMessages.push([el]);
          }

          lastEl = el;
        });
      }
      return formattedMessages;
    },
    messageLoading() {
      return this.$store.getters.getMessageLoadingValue(this.$route.params.id);
    }
  },
  created() {
    this.initProcessing();

    Event.listen("chat-changed", () => {
      this.processing = false;
    });
  },
  mounted() {
    const $this = this;
    
    Event.listen("script-loaded", () => {
      $this.initProcessing();
    });
  },
  updated() {
    if (!this.processing) {
      this.initProcessing();
    }
  }
};
</script>

<style lang="scss">
.chat-page {
  max-width: $grid-max-width;
  margin-right: auto;
  margin-left: auto;
  height: 100vh;

  &--preview {
    .chat-page__wrapper {
      padding-top: 20px;
    }
  }

  &__wrapper {
    padding-bottom: 40px;
    padding-top: 80px;
    overflow-x: auto;
    height: 100vh;

    &--lightbox {
      position: relative;
      z-index: 1000;
    }
  }

  .message-group {
    .message-wrapper {
      margin-bottom: 10px;

      &:last-of-type {
        margin-bottom: 0;
      }
    }

    &.left {
      .message-group__messages {
        .message-wrapper {
          &:nth-child(n + 2) {
            .message {
              border-bottom-left-radius: 7px;
              border-top-left-radius: 7px;
            }
          }

          &:first-of-type:not(:last-of-type) {
            .message {
              border-bottom-left-radius: 7px;
              border-top-left-radius: 20px;
            }
          }

          &:last-of-type {
            &:not(:first-of-type) {
              .message {
                border-bottom-left-radius: 20px;
                border-top-left-radius: 7px;
              }
            }
          }
        }
      }
    }

    &.right {
      .message-group__messages {
        .message-wrapper {
          .message {
            background-color: $color-primary;
            color: $txt-color-alt;
          }

          &:nth-child(n + 2) {
            .message {
              border-bottom-right-radius: 7px;
              border-top-right-radius: 7px;
            }
          }

          &:first-of-type:not(:last-of-type) {
            .message {
              border-bottom-right-radius: 7px;
              border-top-right-radius: 20px;
            }
          }

          &:last-of-type:not(:first-of-type) {
            .message {
              border-bottom-right-radius: 20px;
              border-top-right-radius: 7px;
            }
          }
        }

        &__label {
          text-align: right;
        }

        &__messages {
          align-items: flex-end;
        }
      }
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.4s ease-in-out;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.slide-up-enter-active {
  transition: all 0.3s ease-in-out;
}

.slide-up-leave-active {
  transition: all 0.3s ease-in-out;
  pointer-events: none;
}

.slide-up-enter-to,
.slide-up-leave {
  transform: translate(-50%, 0%);
}

.slide-up-enter,
.slide-up-leave-to {
  transform: translate(-50%, 100%);
  pointer-events: none;
}

.slide-down-enter-active {
  transition: all 0.3s ease-in-out;
}

.slide-down-leave-active {
  transition: all 0.3s ease-in-out;
  pointer-events: none;
}

.slide-down-enter-to,
.slide-down-leave {
  transform: translateY(0%);
}

.slide-down-enter,
.slide-down-leave-to {
  transform: translateY(-100%);
  pointer-events: none;
}
</style>
